import { mergeClasses } from '@fluentui/react-components';
import {
  Card,
  Button,
  Badge,
  Subtitle2,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ShieldCheckmark20Regular } from '@fluentui/react-icons';
import { AscoraJobSummary, SiteContact } from '../types';

interface Props {
  job: AscoraJobSummary;
  siteContact: SiteContact | null;
  contactLoading: boolean;
  onBack: () => void;
  onStartT5: () => void;
}

const useStyles = makeStyles({
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: '16px',
    paddingBottom: '20px',
    paddingLeft: '18px',
    paddingRight: '18px',
  },
  section: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#394456',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    marginTop: '16px',
    marginBottom: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    minHeight: '26px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    boxShadow: 'inset 0 0 0 1px rgba(17,24,36,0.08), 0 8px 18px rgba(17,24,36,0.05)',
  },
  sectionFirst: { marginTop: '0' },
  card: {
    marginBottom: '12px',
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    background: tokens.colorNeutralBackground1,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingTop: '9px',
    paddingBottom: '9px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    fontSize: '13px',
    gap: '8px',
    ':last-child': { borderBottomStyle: 'none' },
  },
  rowKey: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  rowVal: {
    fontWeight: 600,
    textAlign: 'right',
  },
  cta: {
    width: '100%',
    marginTop: '8px',
    minHeight: '52px',
    fontSize: '15px',
    fontWeight: 600,
    borderRadius: '18px',
    boxShadow: '0 10px 22px rgba(227,39,45,0.18)',
  },
  contactSpinner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '12px',
    paddingBottom: '12px',
    color: tokens.colorNeutralForeground3,
    fontSize: '12.5px',
  },
});

function formatCompleted(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

export function JobDetailScreen({ job, siteContact, contactLoading, onStartT5 }: Props) {
  const styles = useStyles();
  const isCompleted = job.takeFiveStatus === 'Completed';

  return (
    <div className={styles.body} data-screen-body>
      <Subtitle2 className={mergeClasses(styles.section, styles.sectionFirst)}>
        Job Information
      </Subtitle2>
      <Card className={styles.card}>
        <div className={styles.row}>
          <span className={styles.rowKey}>Job Name</span>
          <span className={styles.rowVal}>{(job.jobName || '').trim() || 'Untitled job'}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Site Name</span>
          <span className={styles.rowVal}>{job.siteCustomer.name}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowKey}>Site Address</span>
          <span className={styles.rowVal}>
            {job.addressLine1}, {job.suburb} {job.postcode}
          </span>
        </div>
        {job.purchaseOrderNumber && (
          <div className={styles.row}>
            <span className={styles.rowKey}>PO Number</span>
            <span className={styles.rowVal}>{job.purchaseOrderNumber}</span>
          </div>
        )}
        {job.assignedUser && (
          <div className={styles.row}>
            <span className={styles.rowKey}>Assigned</span>
            <span className={styles.rowVal}>{job.assignedUser}</span>
          </div>
        )}
      </Card>

      <Subtitle2 className={styles.section}>Site Contact</Subtitle2>
      <Card className={styles.card}>
        {contactLoading ? (
          <div className={styles.contactSpinner}>
            <Spinner size="extra-tiny" />
            <span>Loading site contact from Ascora…</span>
          </div>
        ) : siteContact ? (
          <>
            <div className={styles.row}>
              <span className={styles.rowKey}>Name</span>
              <span className={styles.rowVal}>{siteContact.fullName || '—'}</span>
            </div>
            {siteContact.role && (
              <div className={styles.row}>
                <span className={styles.rowKey}>Role</span>
                <span className={styles.rowVal}>{siteContact.role}</span>
              </div>
            )}
            {siteContact.bestPhone && (
              <div className={styles.row}>
                <span className={styles.rowKey}>
                  {siteContact.mobile ? 'Mobile' : 'Phone'}
                </span>
                <a
                  className={styles.rowVal}
                  href={`tel:${siteContact.bestPhone.replace(/[^\d+]/g, '')}`}
                  style={{ color: '#C21F24' }}
                >
                  {siteContact.bestPhone}
                </a>
              </div>
            )}
          </>
        ) : (
          <div style={{ paddingTop: 6, paddingBottom: 6, fontSize: 12.5, color: tokens.colorNeutralForeground3 }}>
            No site contact recorded against this job.
          </div>
        )}
      </Card>

      <Subtitle2 className={styles.section}>Take 5 Status</Subtitle2>
      <Card className={styles.card}>
        <div className={styles.row}>
          <span className={styles.rowKey}>Status</span>
          <Badge
            appearance="filled"
            color={isCompleted ? 'success' : 'danger'}
            size="medium"
          >
            {job.takeFiveStatus}
          </Badge>
        </div>
        {isCompleted && job.completedAt && (
          <div className={styles.row}>
            <span className={styles.rowKey}>Completed</span>
            <span className={styles.rowVal}>{formatCompleted(job.completedAt)}</span>
          </div>
        )}
      </Card>

      <Button
        appearance="primary"
        size="large"
        icon={<ShieldCheckmark20Regular />}
        onClick={onStartT5}
        className={styles.cta}
      >
        {isCompleted ? 'Start another Take 5' : 'Start Take 5 Assessment'}
      </Button>
    </div>
  );
}
